import React from "react";
import PropTypes from "prop-types";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  Grid,
  GridItem,
  Button,
  Input,
  Select
} from "@sainsburys-tech/fable";
import { LoadingIndicator } from "@sainsburys-tech/fable";
import ButtonWithApiCall from "../ButtonWithApiCall";
import FormWrapper from "../FormWrapper";
import CbusZoneFormSection from "./CbusZoneFormSection";
import IoZoneFormSection from "./IoZoneFormSection";
import * as zoneTypeKeys from "../../model/lighting-zone-types/zoneTypeKeys.js";

const StoreZoneConfigureForm = props => {
  const validationSchema = Yup.lazy(values => {
    const sharedSchema = Yup.object().shape({
      selectedZone: Yup.string().required("Please select a zone"),
      otherZoneName: Yup.string().when("selectedZone", {
        is: val => val === "Other",
        then: schema => schema
          .trim()
          .label("Other Zone Name")
          .lowercase()
          .test(
            "invalid-zone-name",
            "Zone name is not valid",
            value => value !== "other" && value !== "please select"
          )
          .notOneOf(
            props.existingZoneNames.map(z => z.toLowerCase()),
            "Zone name already exists"
          )
          .required("Please enter a zone name")
      })
    });

    const ioSchema = Yup.object().shape({
      pinId: Yup.number()
        .label("Pin group")
        .typeError("Pin group must be a number")
        .required("Please enter a pin group")
        .notOneOf(props.existingPinIds, "PinId is already in use")
    });

    const cbusSchema = Yup.object().shape({
      cbusGroupId: Yup.number()
        .label("C-Bus Group ID")
        .typeError("C-Bus Group ID must be a number")
        .integer("C-Bus Group ID must be a whole number")
        .min(10)
        .max(254)
        .notOneOf(props.existingCbusIds, "C-Bus Group ID is already in use")
        .test(
          "LuxSensorId must not equal CbusGroupId",
          "C-Bus Group ID and Lux Sensor ID must be different",
          function test(val) {
            return (
              (val === undefined && this.parent.luxSensorId === undefined) ||
              val !== this.parent.luxSensorId
            );
          }
        )
        .required("Please enter a C-Bus Group ID"),

      hasLuxSensor: Yup.string().required("Please select an option"),
      luxSensorId: Yup.mixed().when("hasLuxSensor", {
        is: "true",
        then: () => Yup.number()
          .label("Lux Sensor ID")
          .typeError("Lux Sensor ID must be a number")
          .integer("Lux Sensor ID  must be a whole number")
          .min(10)
          .max(254)
          .notOneOf(props.existingCbusIds, "Lux Sensor ID is already in use")
          .required("Please enter a Lux Sensor ID")
      }),
      pirSensor: Yup.array()
        .of(
          Yup.object().shape({
            name: Yup.string(),
            value: Yup.number()
              .transform((newVal, originalVal) =>
                originalVal === "" ? null : newVal
              )
              .nullable()
              .label("PIR ID")
              .typeError("PIR ID must be a number")
              .integer("PIR ID must be a whole number")
              .min(10)
              .max(254)
          })
        )
        .test("unique", "PIR ID is already in use", function(list) {
          const values = list.map(l => l.value).filter(l => l !== null);
          const set = new Set(values);
          const listContainsDuplicates = values.length !== set.size;
          if (!listContainsDuplicates) {
            return true;
          }

          const currentIndex = values.findIndex((l, i) => l !== [...set][i]);
          return this.createError({
            path: `pirSensor[${currentIndex}]`,
            message: { value: "PIR ID is already in use" }
          });
        })
    });

    switch (values.zoneType) {
      case zoneTypeKeys.IO:
        return sharedSchema.concat(ioSchema);
      case zoneTypeKeys.CBUS:
        return sharedSchema.concat(cbusSchema);
      default:
        return sharedSchema;
    }
  });

  const resetFieldArray = (formProps, field) => {
    formProps.values[field] = [{ name: "pir0", value: null }];
    formProps.setFieldTouched(field, false);
  };

  const resetField = (formProps, field) => {
    formProps.values[field] = "";
    formProps.setFieldTouched(field, false);
  };

  const SelectZoneField = field => {
    const formProps = field.form;
    return (
      <Grid cols={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}>
        <GridItem colSpan={{ xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}>
          <Select
            disabled={props.zoneId.length > 0}
            name="selectedZone"
            label="Select zone name"
            options={props.zoneOptions}
            value={formProps.values.selectedZone}
            status={formProps.touched.selectedZone && formProps.errors.selectedZone ? "error" : undefined}
            onBlur={formProps.handleBlur}
            onChange={e => {
              formProps.handleChange(e);

              const zoneType = props.zones.find(
                z => z.zoneId === e.target.value
              )
                ? props.zones.find(z => z.zoneId === e.target.value).zoneType
                : "";

              if (e.target.value !== "Other") {
                resetField(formProps, "otherZoneName");
              }

              if (formProps.values["zoneType"] !== zoneType) {
                resetField(formProps, "pinId");
                resetField(formProps, "hasLuxSensor");
                resetField(formProps, "luxSensorId");
                resetFieldArray(formProps, "pirSensor");
                resetField(formProps, "cbusGroupId");
              }

              formProps.values["zoneType"] = zoneType;
            }}
          />
        </GridItem>
      </Grid>
    );
  };

  const OtherZoneField = field => {
    const formProps = field.form;
    return (
      <section>
        <Grid cols={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }} gap="md">
          <GridItem colSpan={{ xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}>
            <Input
              className="fable:mb-0"
              name={"otherZoneName"}
              label="Other Zone Name"
              value={formProps.values.otherZoneName}
              status={formProps.touched.otherZoneName && formProps.errors.otherZoneName ? "error" : undefined}
              supportingText={
                (formProps.touched.otherZoneName &&
                  formProps.errors.otherZoneName) ||
                (props.errorCreatingZone &&
                  "There was an error creating this zone")
              }
              onBlur={formProps.handleBlur}
              onChange={formProps.handleChange}
              disabled={props.creatingZone}
            />
          </GridItem>
          <GridItem colSpan={{ xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}>
            <div className="fable:flex">
              <Button
                variant="text"
                onClick={() =>
                  props.onCreateZone(
                    formProps.values.otherZoneName,
                    formProps.values
                  )
                }
                disabled={
                  props.creatingZone ||
                  (formProps.errors.otherZoneName &&
                    formProps.errors.otherZoneName.length > 0)
                }
                className="fable:mr-4 fable:p-3"
              >
                Create
              </Button>
              {props.creatingZone && (
                <LoadingIndicator isLoading isStandalone size="lg"/>
              )}
            </div>
          </GridItem>
        </Grid>
      </section>
    );
  };

  const ZoneSelect = field => {
    const formProps = field.form;
    return (
      <section>
        <Grid cols={{ xl: 2, lg: 3, md: 5, sm: 1, xs: 1 }}>
          <GridItem colSpan={{ xl: 1, lg: 2, md: 4, sm: 1, xs: 1 }}>
            <h3 className="fable:mb-4">Identify the zone</h3>
            <p>
              Select a name from the dropdown or select 'Other' and create a new
              zone if the zone is not listed.
            </p>
          </GridItem>
        </Grid>
        <SelectZoneField form={formProps} />
        {formProps.values.selectedZone === "Other" && (
          <OtherZoneField form={formProps} />
        )}
      </section>
    );
  };

  const SaveAndCancelButtons = field => {
    const formProps = field.form;
    return (
      <Grid cols={{ xl: 4, lg: 3, md: 5, sm: 2, xs: 1 }} gap="md">
        <GridItem colSpan={{ xl: 1, lg: 1, md: 2, sm: 1, xs: 1 }}>
          <fieldset>
            <legend style={{ fontSize: '1rem', fontWeight: '600' }}>Save</legend>
            {props.errorSubmitting && (
              <span style={{ color: '#cc0000', display: 'block', marginBottom: '0.5rem' }}>
                There was an error submitting
              </span>
            )}
            <ButtonWithApiCall
              type="submit"
              onClick={() => props.onSubmit(formProps.values)}
              onComplete={() => props.onSaveComplete(formProps.values)}
              fullWidth
              disabled={
                !formProps.isValid || formProps.values.selectedZone === "Other"
              }
            />
          </fieldset>
        </GridItem>
        <GridItem colSpan={{ xl: 1, lg: 1, md: 2, sm: 1, xs: 1 }}>
          <Button variant="secondary" onClick={props.onCancel} fullWidth>
            Cancel
          </Button>
        </GridItem>
      </Grid>
    );
  };

  return (
    <Formik
      enableReinitialize
      initialValues={props.initialValues}
      isInitialValid={() => {
        let isValid = true;
        try {
          validationSchema.validateSync(props.initialValues);
        } catch (error) {
          isValid = false;
        }
        return isValid;
      }}
      touched={{}}
      validateOnChange={true}
      validationSchema={validationSchema}
      render={formProps => (
        <FormWrapper isDirty={formProps.dirty}>
          <ZoneSelect form={formProps} />
          {formProps.values.zoneType === zoneTypeKeys.CBUS && (
            <CbusZoneFormSection form={formProps} />
          )}
          {formProps.values.zoneType === zoneTypeKeys.IO && (
            <IoZoneFormSection form={formProps} />
          )}
          <SaveAndCancelButtons form={formProps} />
        </FormWrapper>
      )}
    />
  );
};

StoreZoneConfigureForm.propTypes = {
  zoneOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      values: PropTypes.string
    })
  ),
  zones: PropTypes.array,
  existingZoneNames: PropTypes.arrayOf(PropTypes.string),
  existingCbusIds: PropTypes.arrayOf(PropTypes.number),
  existingPinIds: PropTypes.arrayOf(PropTypes.number),
  storeId: PropTypes.string,
  storeConfiguration: PropTypes.shape({
    store: PropTypes.object,
    zones: PropTypes.array
  }),
  onSubmit: PropTypes.func,
  onSaveComplete: PropTypes.func,
  onCancel: PropTypes.func,
  onCreateZone: PropTypes.func,
  initialValues: PropTypes.shape({
    selectedZone: PropTypes.string,
    otherZoneName: PropTypes.string,
    hasLuxSensor: PropTypes.string,
    luxMeterCbusGroupId: PropTypes.string,
    cbusGroupId: PropTypes.string
  }),
  creatingZone: PropTypes.bool
};

export default StoreZoneConfigureForm;
